import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { LanguageErrorCodes, TelSysConfiguration } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    CMEResponse,
    CMEResponseInternal,
    LicenceResponseMapped,
    MiscAppConfigDTO,
    TelSysAdminDTO
} from '../../../NonReact/Types/Types'
import { telSysCreatorURL, telSysUpdateURL } from '../../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'
import { HIDE } from '../../../NonReact/constants/class-names'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { AddUserLoading } from '../../AddUser/AddUserLoading'
import { RESTAPIError } from '../../Common/API'
import { getCME, getCMEInternal, getTelSys } from '../../../NonReact/common/API/EntityAPIClient'
import { getCMELicences, getLicenceValue, getTelSysLicences } from '../../Common/API/Licence/LicenceAPI'
import { ResultType } from '../../../NonReact/common/API/REST/RESTAPIError'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { IconName } from '../../Common/Components/Icon'
import { LicenceManagement } from '../../Common/Components/LicenceManagement/LicenceManagement'
import Tabs from '../../Common/Components/Tabs'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { LanguageContext, MessageContext, ReactState } from '../../Common/Contexts'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { LicenceValue, ProcessedTelSysProfile } from '../../Common/Types/Types'
import {
    DEFAULT_COUNTRY_CODE,
    DEFAULT_ENTERPRISE_USER_EXTENSIONS,
    DEFAULT_INTERNAL_DIGIT_LENGTH,
    DEFAULT_MAX_PARTITION_COUNT,
    DEFAULT_MAX_USERS_MONITORED,
    DEFAULT_MAX_USERS_SYNC,
    DEFAULT_TIMEZONE,
    INTEGRATION_TYPE,
    LicencedForRptTypes,
    TelSysConfigurationModalID,
    TelSysTypes
} from '../Constants/Constants'
import {
    CALL_CENTRE_AGENT_LICENSE_FIELD_NAME,
    CALL_CENTRE_SUPERVISOR_LICENSE_FIELD_NAME,
    CME_FIELD_NAME,
    CX_INTEGRATION_LICENSE_FIELD_NAME,
    ENTERPRISE_LICENSE_FIELD_NAME,
    ESSENTIALS_LICENSE_FIELD_NAME,
    PROFESSIONAL_LICENSE_FIELD_NAME,
    TYPE_FIELD_NAME
} from '../Constants/FieldNames'
import { FooterActions } from '../Footer/FooterActions'
import { mapFromFormToServer, mapFromServerLicencesToForm, mapFromServerToForm } from '../Functions/Mappers'
import { AdvancedTab } from '../Tabs/AdvancedTab'
import { CommunicationsTab } from '../Tabs/CommunicationsTabs'
import { DefaultSiteSettingsTab } from '../Tabs/DefaultSiteSettingsTab'
import { DetailsTab } from '../Tabs/DetailsTab'
import { SyncTab } from '../Tabs/SyncTab'
import { AppDownloads, TelSysForm, TelSysLicenceManagement } from '../Types/Types'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { BackendPermissionNames } from '../../Common/Constants/Permissions'
import { getMiscAppConfig } from '../../Common/API/GenericConfigAPI'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { CXI_FEATURE_FLAG_KEY_FOR_TELSYS } from '../../Common/Constants/FeatureFlags'
import { ENABLE_CRM_TEAMS } from '../../../ReactRouting/Constants/FeatureFlags'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { CapabilityBitFlags, telSysCapabilityBitFlags } from '../../CMEAddModify/Constants'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

type Props = {
    refreshAction: () => void
    closeModal: () => void
    filterCMEnvironmentID: string
    selectedTelsys?: string
    viewOnly?: boolean
}

export const TelSysTabs: FC<Props> = ({
    refreshAction,
    closeModal,
    filterCMEnvironmentID,
    selectedTelsys,
    viewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    language.loadBundle(LanguageErrorCodes.NAME)

    const [userInfo]: UserInfo = useUserInfo()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(
        TelSysConfigurationModalID.TELSYS_DETAILS
    )
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [serverError, setServerError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [commsStatus, setCommsStatus]: ReactState<Nullable<string[]>> = useState<Nullable<string[]>>()
    const [syncStatus, setSyncStatus]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const isCrmTeamsEnabled: Definable<boolean> = useFeatureFlag(ENABLE_CRM_TEAMS)

    const [availableTelSysProfiles, setAvailableTelSysProfiles]: ReactState<Nullable<ProcessedTelSysProfile[]>> =
        useState<Nullable<ProcessedTelSysProfile[]>>()

    const [cmeCapabilities, setCMECapabilities]: ReactState<number> = useState<number>(0)
    const [CMELicenseCap, setCMELicenseCap]: ReactState<Nullable<LicenceResponseMapped>> = useState<
        Nullable<LicenceResponseMapped>
    >({
        UsedLicences: {
            Essentials: null,
            Professional: null,
            Enterprise: null,
            CallCentreAgent: null,
            CallCentreSupervisor: null,
            CXIntegration: null
        },
        AvailableLicences: {
            Essentials: null,
            Professional: null,
            Enterprise: null,
            CallCentreAgent: null,
            CallCentreSupervisor: null,
            CXIntegration: null
        },
        MaxLicences: {
            Essentials: null,
            Professional: null,
            Enterprise: null,
            CallCentreAgent: null,
            CallCentreSupervisor: null,
            CXIntegration: null
        }
    })

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const firstLoad: MutableRefObject<boolean> = useRef<boolean>(true)

    const [originalLicenseManagementValues, setOriginalLicenseManagementValues]: ReactState<
        Nullable<TelSysLicenceManagement<Nullable<number>>>
    > = useState<Nullable<TelSysLicenceManagement<Nullable<number>>>>()
    const [voiceAppBotsDisabled, setVoiceAppBotsDisabled]: ReactState<Nullable<boolean>> = useState<Nullable<boolean>>()
    const [appDownloadURLs, setAppDownloadURLs]: ReactState<AppDownloads> = useState<AppDownloads>({
        CXANativeTeamsAppURL: EMPTY_STRING,
        CXIClientWindowsURL: EMPTY_STRING,
        CXIClientMacURL: EMPTY_STRING,
        SearchCallTeamsAppURL: EMPTY_STRING
    })

    const [licenceSummary, setLicenceSummary]: ReactState<Nullable<LicenceResponseMapped>> =
        useState<Nullable<LicenceResponseMapped>>(undefined)

    const isEditMode: boolean = useMemo((): boolean => isStringNotEmpty(selectedTelsys), [selectedTelsys])

    const [originalTelSysServer, setOriginalTelSysServer]: ReactState<JSONResponse<TelSysAdminDTO>> =
        useState<JSONResponse<TelSysAdminDTO>>(null)

    const defaultValues: MutableRefObject<TelSysForm> = useRef({
        details: {
            id: EMPTY_STRING,
            description: EMPTY_STRING,
            cme: EMPTY_STRING,
            tags: EMPTY_STRING,
            billingID: EMPTY_STRING,
            billable: true,
            trial: false,
            eccrReplacement: false,
            aiAssistEnabled: false,
            mfa: false
        },
        communications: {
            type: TelSysTypes.UNKNOWN,
            licencedFor: LicencedForRptTypes.TWO_THOUSAND,
            integrationType: INTEGRATION_TYPE.BUSINESS,
            commsEnabled: false,
            inService: false
        },
        sync: {
            syncNow: false,
            lastSync: EMPTY_STRING
        },
        defaultSiteSettings: {
            timezone: DEFAULT_TIMEZONE,
            countryCode: DEFAULT_COUNTRY_CODE,
            internalDigitLength: DEFAULT_INTERNAL_DIGIT_LENGTH,
            outboundTelNoPrefix: EMPTY_STRING,
            recordingIntegration: RecordingIntegrations.NONE,
            e164Enabled: true
        },
        licenseManagement: {
            essentials: EMPTY_STRING,
            professional: EMPTY_STRING,
            enterprise: EMPTY_STRING,
            callCentreAgent: EMPTY_STRING,
            callCentreSupervisor: EMPTY_STRING,
            cxIntegration: EMPTY_STRING
        },
        advanced: {
            partitionCountMax: DEFAULT_MAX_PARTITION_COUNT,
            scheduledReportsAllowedPerUser: EMPTY_STRING,
            scheduledEmailsAllowedPerReport: EMPTY_STRING,
            ctiLogIncClassNames: false,
            internalDiagDumpEnabled: false,
            callModelDiagDumpEnabled: false,
            ctiLogEnabled: false,
            partitionsRptingScopeAllEnabled: false,
            callModelDiagDumpEnabledForDev: EMPTY_STRING,
            ctiCallModelLogEnabled: false,
            userNotificationsEnabled: false,
            extensionsPerEnterpriseUser: DEFAULT_ENTERPRISE_USER_EXTENSIONS,
            maximumAllowedUsersSync: DEFAULT_MAX_USERS_SYNC,
            maximumAllowedUsersMonitored: DEFAULT_MAX_USERS_MONITORED
        }
    })

    // Form context.
    const methods: UseFormMethods<TelSysForm> = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    // eslint-disable-next-line
    const { reset, handleSubmit, errors, watch, trigger, setValue }: UseFormMethods<TelSysForm> = methods

    const telSysType: TelSysTypes = watch(TYPE_FIELD_NAME)
    const cmeID: string = watch(CME_FIELD_NAME)

    const isCxiEnabled: boolean = useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[telSysType], false)

    const previousCMEValue: MutableRefObject<string> = useRef<string>(cmeID)

    const validationErrors: boolean[] = useMemo(
        (): boolean[] => [
            !!errors.details,
            !!errors.communications,
            !!errors.defaultSiteSettings,
            !!errors.advanced,
            !!errors.licenseManagement
        ],
        [JSON.stringify(errors)]
    )

    const hasTelsysSyncRestrictionPerms: boolean = useMemo(
        (): boolean =>
            PermissionsUtil.hasPermissions(
                userInfo,
                PermissionsUtil.builder(BackendPermissionNames.TELSYS_SYNC_RESTRICTIONS).allowed()
            ) ?? false,
        [JSON.stringify(userInfo?.Permissions?.Admin)]
    )

    const getTelephonyServer = useCallback((): void => {
        setIsLoading(true)

        Promise.all([getTelSys(selectedTelsys ?? EMPTY_STRING), getTelSysLicences(selectedTelsys ?? EMPTY_STRING)])
            .then(([telSys, telSysLicences]: [JSONResponse<TelSysAdminDTO>, Nullable<LicenceResponseMapped>]): void => {
                reset(mapFromServerToForm(telSys, setCommsStatus, setSyncStatus))
                setOriginalLicenseManagementValues(mapFromServerLicencesToForm(telSys))
                setVoiceAppBotsDisabled(telSys?.BotsDisabled ?? undefined)
                setOriginalTelSysServer(telSys)

                setLicenceSummary(telSysLicences)
                setIsLoading(false)
            })
            .catch(errorHandler)
    }, [selectedTelsys])

    // List of all tabs settings.
    let tabs: Nullable<Tab[]>

    const isCxiCapable: boolean = useMemo((): boolean => {
        const bitFlag: CapabilityBitFlags = telSysCapabilityBitFlags[telSysType]
        if (!bitFlag) return false
        return isBitmaskMatching(cmeCapabilities, bitFlag)
    }, [cmeCapabilities, telSysType])

    if (!isLoading && userInfo) {
        tabs = [
            {
                ID: TelSysConfigurationModalID.TELSYS_DETAILS,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_Details),
                    iconName: IconName.TELSYS_DETAILS,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_DETAILS)
                },
                content: (
                    <DetailsTab
                        filterCMEnvironmentID={filterCMEnvironmentID}
                        commsStatus={commsStatus}
                        viewOnly={viewOnly}
                        isEdit={isStringNotEmpty(selectedTelsys)}
                        cxiAccessCode={
                            isCxiCapable && isCxiEnabled ? originalTelSysServer?.CXIAccessCode ?? null : null
                        }
                    />
                ),
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_DETAILS]
            },
            {
                ID: TelSysConfigurationModalID.TELSYS_COMMUNICATIONS,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_Communication),
                    iconName: IconName.TELSYS_COMMUNICATIONS,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_COMMUNICATIONS)
                },
                content: (
                    <CommunicationsTab
                        viewOnly={viewOnly}
                        isEditMode={isStringNotEmpty(selectedTelsys)}
                        selectedTelSys={selectedTelsys}
                        syncStatus={syncStatus}
                        originalTelSys={originalTelSysServer}
                        telSysLicenceManagement={licenceSummary}
                        availableTelSysProfiles={availableTelSysProfiles}
                        setAvailableTelSysProfiles={setAvailableTelSysProfiles}
                        cmeCapabilities={cmeCapabilities}
                    />
                ),
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_COMMUNICATIONS]
            },
            {
                ID: TelSysConfigurationModalID.TELSYS_SYNC,
                isDisplayed: isStringNotEmpty(selectedTelsys) && telSysType === TelSysTypes.PANASONIC_TDA_NCP,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_Sync),
                    iconName: IconName.SYNC,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_SYNC)
                },
                content: <SyncTab viewOnly={viewOnly} syncStatus={syncStatus} />,
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_SYNC]
            },
            {
                ID: TelSysConfigurationModalID.TELSYS_DEFAULT_SITE_SETTINGS,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_DefaultSiteSettings),
                    iconName: IconName.SETTINGS_SUGGEST,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_DEFAULT_SITE_SETTINGS)
                },
                content: <DefaultSiteSettingsTab viewOnly={viewOnly} selectedTelSys={selectedTelsys} />,
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_DEFAULT_SITE_SETTINGS]
            },
            {
                ID: TelSysConfigurationModalID.TELSYS_LICENSE_MANAGEMENT,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_LicenseManagement),
                    iconName: IconName.LICENCES,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_LICENSE_MANAGEMENT)
                },
                content: (
                    <LicenceManagement
                        methods={methods}
                        limitations={CMELicenseCap}
                        originalLicenseManagementValues={originalLicenseManagementValues}
                        isCxiAvailable={isCxiEnabled && isCxiCapable}
                        formNames={{
                            essentials: ESSENTIALS_LICENSE_FIELD_NAME,
                            professional: PROFESSIONAL_LICENSE_FIELD_NAME,
                            enterprise: ENTERPRISE_LICENSE_FIELD_NAME,
                            callCentreAgent: CALL_CENTRE_AGENT_LICENSE_FIELD_NAME,
                            callCentreSupervisor: CALL_CENTRE_SUPERVISOR_LICENSE_FIELD_NAME,
                            cxIntegration: CX_INTEGRATION_LICENSE_FIELD_NAME
                        }}
                        isViewOnly={viewOnly}
                    />
                ),
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_LICENSE_MANAGEMENT],
                isDisplayed:
                    watch(TYPE_FIELD_NAME) === TelSysTypes.MS_TEAMS && userInfo?.Scope?.TelSysID?.[0] === ALL_VALUE
            },
            {
                ID: TelSysConfigurationModalID.TELSYS_ADVANCED,
                tabsMenuItem: {
                    title: language.get(TelSysConfiguration.TelSysConfiguration_Tabs_Advanced),
                    iconName: IconName.WRENCH,
                    onClick: (): void => activateTab(TelSysConfigurationModalID.TELSYS_ADVANCED)
                },
                content: (
                    <AdvancedTab
                        viewOnly={viewOnly}
                        isEditMode={isEditMode}
                        selectedTelSysID={selectedTelsys}
                        voiceAppBotsDisabled={voiceAppBotsDisabled}
                        getTelephoneServer={getTelephonyServer}
                        availableTelSysProfiles={availableTelSysProfiles}
                        hasTelsysSyncRestrictionPerms={hasTelsysSyncRestrictionPerms}
                    />
                ),
                hasError: validationErrors[TelSysConfigurationModalID.TELSYS_ADVANCED]
            }
        ]
    }

    /**
     * Tab activation method.
     *
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const errorHandler = (e: RESTAPIError): void => {
        displayError(e)

        if (isMounted.current) {
            setIsSubmitting(false)
            setIsLoading(false)
        }
    }

    const submitUserConfiguration = (state: TelSysForm): void => {
        setIsSubmitting(true)

        mapFromFormToServer(state, hasTelsysSyncRestrictionPerms)
            .then((telSys: TelSysAdminDTO): void => {
                const successfulSubmit = (): void => {
                    closeModal()
                    refreshAction()
                }

                const failedSubmit = (error: RESTAPIError): void => {
                    const { errorCode, errorMessage, HTTPStatus, resultType }: RESTAPIError = error

                    if (resultType === ResultType.Partial) {
                        displayError(error)
                        closeModal()

                        setTimeout((): void => {
                            refreshAction()
                        }, 3000)

                        return undefined
                    }

                    if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                        displayError(error)

                        return successfulSubmit()
                    }

                    const errorCodeLanguage: Nullable<string> = (
                        LanguageErrorCodes as unknown as Record<string, string>
                    )[`Errorcode${errorCode}`]

                    setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
                    setIsSubmitting(false)

                    return undefined
                }

                if (IsStringEmpty(selectedTelsys)) {
                    requestJSON(telSysCreatorURL, {
                        method: RESTMethods.POST,
                        body: telSys
                    })
                        .then(successfulSubmit)
                        .catch(failedSubmit)
                } else {
                    requestJSON(telSysUpdateURL(selectedTelsys), {
                        method: RESTMethods.PUT,
                        body: telSys
                    })
                        .then(successfulSubmit)
                        .catch(failedSubmit)
                }
            })
            .catch(errorHandler)
    }

    const invalidFormSubmit = (): void => {
        setActiveTabID(validationErrors.findIndex((validationError: boolean): boolean => validationError))
    }

    useEffect((): void => {
        if (isStringNotEmpty(selectedTelsys)) {
            getTelephonyServer()
        } else if (filterCMEnvironmentID && !selectedTelsys) {
            setIsLoading(true)

            setOriginalLicenseManagementValues({
                essentials: 0,
                professional: 0,
                enterprise: 0,
                callCentreAgent: 0,
                callCentreSupervisor: 0,
                cxIntegration: 0
            })

            getLicenceValue('BillingTagDefaultTelSrvs').then((defaultBillingID: Nullable<LicenceValue>): void => {
                reset({
                    ...defaultValues.current,
                    details: {
                        ...defaultValues.current.details,
                        cme: filterCMEnvironmentID,
                        billingID: defaultBillingID?.Value ?? EMPTY_STRING
                    }
                })

                setTimeout((): void => {
                    setIsLoading(false)
                }, 100)
            })
        } else {
            setOriginalLicenseManagementValues({
                essentials: 0,
                professional: 0,
                enterprise: 0,
                callCentreAgent: 0,
                callCentreSupervisor: 0,
                cxIntegration: 0
            })
        }
    }, [filterCMEnvironmentID, selectedTelsys])

    useEffect((): void => {
        if (isStringNotEmpty(cmeID) && telSysType === TelSysTypes.MS_TEAMS) {
            const isCMEChanged: boolean = cmeID !== previousCMEValue.current

            if (isCMEChanged) {
                previousCMEValue.current = cmeID
            }

            getCMELicences(cmeID)
                .then((licenses: Nullable<LicenceResponseMapped>): void => {
                    setCMELicenseCap(licenses)

                    const essentialsLicenceCapValue: Nullable<string> = watch(ESSENTIALS_LICENSE_FIELD_NAME)
                    const professionalLicenceCapValue: Nullable<string> = watch(PROFESSIONAL_LICENSE_FIELD_NAME)
                    const enterpriseLicenceCapValue: Nullable<string> = watch(ENTERPRISE_LICENSE_FIELD_NAME)
                    const callCentreAgentLicenceCapValue: Nullable<string> = watch(CALL_CENTRE_AGENT_LICENSE_FIELD_NAME)
                    const callCentreSupervisorLicenceCapValue: Nullable<string> = watch(
                        CALL_CENTRE_SUPERVISOR_LICENSE_FIELD_NAME
                    )

                    if (
                        (isStringNotEmpty(selectedTelsys) && isVarNotEmpty(originalLicenseManagementValues)) ||
                        IsStringEmpty(selectedTelsys)
                    ) {
                        if (
                            (IsStringEmpty(essentialsLicenceCapValue) &&
                                IsStringEmpty(professionalLicenceCapValue) &&
                                IsStringEmpty(enterpriseLicenceCapValue) &&
                                IsStringEmpty(callCentreAgentLicenceCapValue) &&
                                IsStringEmpty(callCentreSupervisorLicenceCapValue)) ||
                            isCMEChanged
                        ) {
                            setValue(
                                ESSENTIALS_LICENSE_FIELD_NAME,
                                licenses?.AvailableLicences?.Essentials ?? EMPTY_STRING
                            )
                            setValue(
                                PROFESSIONAL_LICENSE_FIELD_NAME,
                                licenses?.AvailableLicences?.Professional ?? EMPTY_STRING
                            )
                            setValue(
                                ENTERPRISE_LICENSE_FIELD_NAME,
                                licenses?.AvailableLicences?.Enterprise ?? EMPTY_STRING
                            )
                            setValue(
                                CALL_CENTRE_AGENT_LICENSE_FIELD_NAME,
                                licenses?.AvailableLicences?.CallCentreAgent ?? EMPTY_STRING
                            )
                            setValue(
                                CALL_CENTRE_SUPERVISOR_LICENSE_FIELD_NAME,
                                licenses?.AvailableLicences?.CallCentreSupervisor ?? EMPTY_STRING
                            )
                        }
                    }

                    if (!firstLoad.current) {
                        setTimeout((): void => {
                            trigger(ESSENTIALS_LICENSE_FIELD_NAME)
                            trigger(PROFESSIONAL_LICENSE_FIELD_NAME)
                            trigger(ENTERPRISE_LICENSE_FIELD_NAME)
                            trigger(CALL_CENTRE_AGENT_LICENSE_FIELD_NAME)
                            trigger(CALL_CENTRE_SUPERVISOR_LICENSE_FIELD_NAME)
                        })
                    } else {
                        firstLoad.current = false
                    }
                })
                .catch((e: RESTAPIError): void => errorHandler(e))
        }
    }, [cmeID, telSysType, originalLicenseManagementValues])

    useEffect((): void => {
        if (isStringNotEmpty(cmeID) && telSysType === TelSysTypes.MS_TEAMS) {
            Promise.all([getMiscAppConfig(), getCMEInternal(cmeID)])
                .then(([miscAppConfig, cme]: [JSONResponse<MiscAppConfigDTO>, Nullable<CMEResponseInternal>]): void => {
                    if (isMounted.current) {
                        setAppDownloadURLs({
                            CXANativeTeamsAppURL:
                                cme?.AppDownloadURL ?? miscAppConfig?.teamsAppDownloadURL ?? EMPTY_STRING,
                            CXIClientWindowsURL: isCrmTeamsEnabled
                                ? cme?.CXIClientDownloadURLWin ?? EMPTY_STRING
                                : EMPTY_STRING,
                            CXIClientMacURL: isCrmTeamsEnabled
                                ? cme?.CXIClientDownloadURLMac ?? EMPTY_STRING
                                : EMPTY_STRING,
                            SearchCallTeamsAppURL: isCrmTeamsEnabled
                                ? cme?.SearchAndCallAppDownloadURL ?? EMPTY_STRING
                                : EMPTY_STRING
                        })
                    }
                })
                .catch(displayError)
        }
    }, [cmeID, telSysType, isCrmTeamsEnabled])

    useEffect((): void => {
        if (isStringNotEmpty(cmeID)) {
            getCME(cmeID).then((cme: Nullable<CMEResponse>): void => setCMECapabilities(cme?.Capabilities ?? 0))
        }
    }, [cmeID])

    return (
        <div className='configuration-tabs'>
            {isLoading || isSubmitting || !userInfo ? <AddUserLoading /> : <></>}
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitUserConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    {!viewOnly ? (
                        <FooterActions
                            telSysType={telSysType}
                            closeModal={closeModal}
                            serverError={serverError}
                            appDownloads={{
                                CXANativeTeamsAppURL: appDownloadURLs?.CXANativeTeamsAppURL,
                                CXIClientWindowsURL: appDownloadURLs?.CXIClientWindowsURL,
                                CXIClientMacURL: appDownloadURLs?.CXIClientMacURL,
                                SearchCallTeamsAppURL: appDownloadURLs?.SearchCallTeamsAppURL
                            }}
                            isCrmTeamsEnabled={isCrmTeamsEnabled}
                        />
                    ) : (
                        <></>
                    )}
                </form>
            </FormProvider>
        </div>
    )
}
